import { FC } from 'react';
import { ConfigProvider, Form, Tooltip } from 'antd';
import { useFormikContext } from 'formik';
import cn from 'classnames';

import {
	Button,
	Checkbox,
	FormikCheckbox,
	FormikCheckboxGroup,
	FormikSelectWithoutChoice,
	Icon,
	Text
} from 'shared/components/ui';
import { SearchByV2, SearchSelect } from 'features/search/components';
import {
	useGetCompaniesQuery,
	useGetCountriesQuery,
	useGetLanguagesQuery,
	useGetLocationsQuery,
	useGetRegionsQuery,
	useGetSchoolsQuery,
	useGetSubregionsQuery
} from 'services';
import { SeniorityEnum, YoEmploymentEnum, YoExperienceEnum } from 'shared/generated-models';
import { SearchFormParams } from 'features/search/models';
import { AdaptiveDevices, formIsEmpty, getAdaptiveDevice } from 'shared/utils';
import { environment } from 'environments/environment';

import styles from './index.module.scss';

interface Props {
	variant?: 'modal' | 'main';
}

export const SearchFormV3: FC<Props> = ({ variant }: Props) => {
	const device = getAdaptiveDevice();
	const isSmallDevice = device === AdaptiveDevices.MOBILE || device === AdaptiveDevices.TABLET;
	const tooltipPlacement = isSmallDevice ? 'top' : 'right';

	const { values, setValues, validateForm, handleSubmit } = useFormikContext<SearchFormParams>();

	const buttonsDisabled = formIsEmpty({ ...values, size: undefined, page: undefined });

	const handleClearFilters = () => {
		setValues({});
		validateForm({});
	};

	return (
		<ConfigProvider
			theme={{
				components: {
					Tooltip: {
						paddingSM: 0,
						paddingXS: 0,
						borderRadius: 2,
						colorBgDefault: '#f8f8f8'
					}
				}
			}}>
			<div className={styles.container}>
				<Form layout="vertical" className={styles.form}>
					<div className={styles.block}>
						<div className={styles.blockHeader}>
							<Icon icon="terminal2" size={24} />
							<Text variant="inter/15/semi">TECHNICAL REQUIREMENTS</Text>
						</div>
						<SearchByV2 />
					</div>
					<div className={styles.block}>
						<div className={styles.blockHeader}>
							<Icon icon="location" size={24} />
							<Text variant="inter/15/semi">LOCATION</Text>
						</div>
						<div className={styles.blockBody}>
							<div className={styles.inline}>
								<SearchSelect
									label="City or state"
									name="locations"
									useQuery={useGetLocationsQuery}
									placeholder="Enter City or state"
								/>
								<SearchSelect
									label="Country"
									name="countries"
									useQuery={useGetCountriesQuery}
									placeholder="Enter Country"
								/>
							</div>
							<div className={styles.inline}>
								<SearchSelect
									label="Region"
									name="regions"
									useQuery={useGetRegionsQuery}
									placeholder="Enter Region"
								/>
								<SearchSelect
									label="Subregion"
									name="sub_regions"
									useQuery={useGetSubregionsQuery}
									placeholder="Enter Subregion"
								/>
							</div>
						</div>
					</div>
					<div className={styles.block}>
						<div className={styles.blockHeader}>
							<Icon icon="work" size={24} />
							<Text variant="inter/15/semi">EXPERIENCE</Text>
						</div>
						<div className={styles.blockBody}>
							<FormikSelectWithoutChoice
								label={
									<div className={styles.label}>
										<Text variant="inter/15/medium">Current job title</Text>
										<Tooltip
											arrow={false}
											title={
												<div className={styles.tooltip}>
													<Text color="dark_green">
														Add keywords mentioned in job title, for example “Staff
														Engineer” or “Marketing”
													</Text>
												</div>
											}
											placement={tooltipPlacement}
											overlayClassName={styles.labelTooltip}>
											<Icon icon="info" className={styles.labelIcon} />
										</Tooltip>
									</div>
								}
								name="current_job_title"
								placeholder="Enter current job title"
							/>
							<FormikCheckbox
								label={
									<div className={styles.label}>
										<Text variant="inter/15/medium">Likely to move</Text>
										<Tooltip
											arrow={false}
											title={
												<div className={styles.tooltip}>
													<Text color="dark_green">
														Show candidates who are more likely to respond to new job
														opportunities
													</Text>
												</div>
											}
											placement={tooltipPlacement}
											overlayClassName={styles.labelTooltip}>
											<Icon icon="info" className={styles.labelIcon} />
										</Tooltip>
									</div>
								}
								name="likely_to_move_prob">
								Likely to move
							</FormikCheckbox>
							<FormikCheckboxGroup
								label={<Text variant="inter/15/medium">Seniority</Text>}
								name="seniority">
								<Checkbox value={SeniorityEnum.SENIOR}>Senior</Checkbox>
								<Checkbox value={SeniorityEnum.MIDDLE}>Middle</Checkbox>
								<Checkbox value={SeniorityEnum.JUNIOR}>Junior</Checkbox>
								<div className={styles.rockstar}>
									<Checkbox value={SeniorityEnum.ROCK_STAR}>
										<span className={styles.rockstarLabel}>Rock Star</span>
									</Checkbox>
									<Tooltip
										arrow={false}
										title={
											<div className={styles.tooltip}>
												<Text color="dark_green">
													Rock stars are famous experts in their areas, such as Dan Abramov or
													Vitalik Buterin.
												</Text>
											</div>
										}
										placement={tooltipPlacement}
										overlayClassName={styles.labelTooltip}>
										<Icon icon="info" className={styles.labelIcon} />
									</Tooltip>
								</div>
							</FormikCheckboxGroup>
							<div className={styles.inline}>
								<div className={styles.field}>
									<FormikCheckboxGroup
										name="yo_employment"
										label={
											<div className={cn(styles.label, styles.rockstarLabel)}>
												<Text variant="inter/15/medium">Years of employment in software</Text>
												<Tooltip
													arrow={false}
													title={
														<div className={styles.tooltip}>
															<Text color="dark_green">
																Based on their work experience on Linkedin
															</Text>
														</div>
													}
													placement={tooltipPlacement}
													overlayClassName={styles.labelTooltip}>
													<Icon icon="info" className={styles.labelIcon} />
												</Tooltip>
											</div>
										}>
										<Checkbox value={YoEmploymentEnum._10_}>10+</Checkbox>
										<Checkbox value={YoEmploymentEnum._5_10}>5-10</Checkbox>
										<Checkbox value={YoEmploymentEnum._3_5}>3-5</Checkbox>
										<Checkbox value={YoEmploymentEnum._0_3}>0-3</Checkbox>
									</FormikCheckboxGroup>
								</div>
								<div className={styles.field}>
									<FormikCheckboxGroup
										name="yo_experience"
										label={
											<div className={cn(styles.label, styles.rockstarLabel)}>
												<Text variant="inter/15/medium">Years of coding experience</Text>
												<Tooltip
													arrow={false}
													title={
														<div className={styles.tooltip}>
															<Text color="dark_green">
																Based on their contributions on Github
															</Text>
														</div>
													}
													placement={tooltipPlacement}
													overlayClassName={styles.labelTooltip}>
													<Icon icon="info" className={styles.labelIcon} />
												</Tooltip>
											</div>
										}>
										<Checkbox value={YoExperienceEnum._10_}>10+</Checkbox>
										<Checkbox value={YoExperienceEnum._5_10}>5-10</Checkbox>
										<Checkbox value={YoExperienceEnum._3_5}>3-5</Checkbox>
										<Checkbox value={YoExperienceEnum._0_3}>0-3</Checkbox>
									</FormikCheckboxGroup>
								</div>
							</div>
							<SearchSelect
								label="Companies"
								name="companies"
								useQuery={useGetCompaniesQuery}
								placeholder="Enter company name or domain"
							/>
							<FormikCheckbox name="is_current_companies">
								<Text variant="inter/14/regular" color="grey">
									Currently working in the company
								</Text>
							</FormikCheckbox>
							<SearchSelect
								label="Excluded companies"
								name="companies_to_exclude"
								useQuery={useGetCompaniesQuery}
								placeholder="Enter or select company name or domain to exclude from search"
							/>
							<FormikCheckbox name="is_current_companies_to_exclude">
								<Text variant="inter/14/regular" color="grey">
									Currently not working in the company
								</Text>
							</FormikCheckbox>
						</div>
					</div>
					<div className={styles.block}>
						<div className={styles.blockHeader}>
							<Icon icon="book" size={24} className={styles.book} />
							<Text variant="inter/15/semi">EDUCATION</Text>
						</div>
						<div className={styles.blockBody}>
							<FormikCheckbox
								label={
									<div className={styles.label}>
										<Text variant="inter/15/medium">Top Schools</Text>
										<Tooltip
											arrow={false}
											title={
												<div className={styles.tooltip}>
													<Text color="dark_green">
														Find candidates who graduated from top schools in computer
														science
													</Text>
												</div>
											}
											placement={tooltipPlacement}
											overlayClassName={styles.labelTooltip}>
											<Icon icon="info" className={styles.labelIcon} />
										</Tooltip>
									</div>
								}
								name="is_top_school">
								Top schools in computer science
							</FormikCheckbox>
							<SearchSelect
								label="Education"
								name="schools"
								useQuery={useGetSchoolsQuery}
								placeholder="Enter or select Schools, Universities"
							/>
						</div>
					</div>
					<div className={styles.block}>
						<div className={styles.blockHeader}>
							<Icon icon="people" size={24} />
							<Text variant="inter/15/semi">LANGUAGE AND DIVERSITY</Text>
						</div>
						<div className={styles.blockBody}>
							<SearchSelect
								label="Languages"
								name="languages"
								useQuery={useGetLanguagesQuery}
								placeholder="Enter or select Languages"
							/>

							<FormikCheckbox
								label={
									<div className={styles.label}>
										<Text variant="inter/15/medium">Diversity filters</Text>
									</div>
								}
								name="is_first_name_female">
								Name contains a female first name
							</FormikCheckbox>
						</div>
					</div>
					{environment.env === 'development' && (
						<div className={styles.block}>
							<div className={styles.blockHeader}>
								<Icon icon="award" size={24} />
								<Text variant="inter/15/semi">ACHIEVEMENTS</Text>
							</div>
							<div className={styles.blockBody}>
								<FormikSelectWithoutChoice
									label={
										<div className={styles.label}>
											<Text variant="inter/15/medium">Awards</Text>
										</div>
									}
									name="awards"
									placeholder="Enter keywords"
								/>
								<FormikSelectWithoutChoice
									label={
										<div className={styles.label}>
											<Text variant="inter/15/medium">Publications</Text>
										</div>
									}
									name="publications"
									placeholder="Enter keywords"
								/>
								<FormikSelectWithoutChoice
									label={
										<div className={styles.label}>
											<Text variant="inter/15/medium">Patents</Text>
										</div>
									}
									name="patents"
									placeholder="Enter keywords"
								/>
								<FormikSelectWithoutChoice
									label={
										<div className={styles.label}>
											<Text variant="inter/15/medium">Certifications</Text>
										</div>
									}
									name="certifications"
									placeholder="Enter keywords"
								/>
							</div>
						</div>
					)}
					{variant === 'modal' && (
						<div className={styles.formButtons}>
							<Button
								className={styles.formButton}
								disabled={buttonsDisabled}
								onClick={handleClearFilters}>
								Clear filters
							</Button>
							<Button
								className={styles.formButton}
								type="primary"
								disabled={buttonsDisabled}
								onClick={handleSubmit}>
								Show candidates
							</Button>
						</div>
					)}
				</Form>
			</div>
		</ConfigProvider>
	);
};
